import React from 'react';

import {isWorkflowCreateTaskFormsEnabled} from 'forms/FormsPermissions';
import {ProcessExecutionType} from 'graphql-client/__generated/graphqlGlobalTypes';
import ErrorList from 'ui-library/spec-editor/ErrorList';

import {FormChangeBanner, ProcessSchemaFieldTable} from './ProcessSchemaFieldComponents';
import type {ResultSchemasGQL, StorableSchemaGQL} from './ProcessSchemaFieldComponents';
import {ProcessTaskSchemaEditorContentSections} from './ProcessTaskSchemaEditorConstants';
import ProcessTaskSchemaEditorSection from './ProcessTaskSchemaEditorSection';
import type {ProcessTaskSchema} from './ProcessTaskSchemaUtils';
import TaskMetadataRows from './TaskMetadataRows';
import type {ProcessTaskSchemaEditorStateResult} from './useProcessTaskSchemaEditorState';

interface TaskConfigSectionProps {
  state: ProcessTaskSchemaEditorStateResult;
  processTaskSchema: ProcessTaskSchema | null;
  storableSchemas: Array<StorableSchemaGQL> | null;
  resultSchemas: Array<ResultSchemasGQL> | null;
  registryId?: string;
  noSectionSpacing?: boolean;
  isSettingsPage?: boolean;
  restrictedToOrganizationId?: string;
  restrictedToExecutionType?: ProcessExecutionType;
}

export default function TaskConfigSection(props: TaskConfigSectionProps): JSX.Element {
  const {state, processTaskSchema} = props;

  return (
    <ProcessTaskSchemaEditorSection
      sectionTitle="Task"
      sectionNumber={1}
      shouldHideSectionHeader={state.shouldHideSectionHeaders}
      noSectionSpacing={props.noSectionSpacing}
    >
      <TaskMetadataRows
        state={state}
        processTaskSchema={processTaskSchema}
        restrictedToOrganizationId={props.restrictedToOrganizationId}
        restrictedToExecutionType={props.restrictedToExecutionType}
      />
      {!state.shouldHideSection(ProcessTaskSchemaEditorContentSections.TASK_FIELDS) && (
        <>
          <ErrorList errorMessage={state.errors.taskFields} />
          <ProcessSchemaFieldTable
            isDisabled={state.isArchived || state.isEditingFlowchart || state.isLocked}
            key={state.taskFieldTableKey}
            hasBottomLink={!state.hasOutputSchema}
            schema={processTaskSchema ?? null}
            ownerUrlId={state.schemaOwner!.url_identifier}
            ownerId={state.schemaOwner!.id}
            registryId={props.registryId}
            onFieldsChange={(updatedTaskFields) => {
              state.onFieldsChanged(state.taskFields, updatedTaskFields, 'taskFields', state.setTaskFields);
            }}
            schemaType="task"
            storableSchemas={props.storableSchemas}
            fields={state.taskFields}
            resultSchemas={props.resultSchemas}
            dataTestAttrs={{component: 'ProcessTaskSchemaEditor', element: 'taskTable'}}
            changeWarnings={
              isWorkflowCreateTaskFormsEnabled() && !state.isBeingCreated && state.validRelevantForms.length
                ? [<FormChangeBanner numRelevantForms={state.validRelevantForms.length} />]
                : undefined
            }
            compactMode={props.isSettingsPage === false}
          />
        </>
      )}
    </ProcessTaskSchemaEditorSection>
  );
}
